import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styles from './index.module.scss'
import cn from 'classnames'
import { Link, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import NormalLayoutContainer from '../shared/NormalLayoutContainer'
import { Spinner } from '../shared/Spinner'
import PageTitle from '../../components/page-title'
import InvoicesSearchForm from '../../components/invoice/invoices-search-form'
import InvoicesTable from '../../components/invoice/InvoicesTable'
import { ROUTES } from '../../routes'
import { ObjectParam, StringParam, useQueryParams } from 'use-query-params'
import QUERY_KEYS from '../../graphql/queryKeys'
import invoicesQuery from '../../graphql/queries/invoices'
import invoiceSettingsQuery from '../../graphql/queries/invoiceSettings'
import exportInvoices from '../../graphql/mutations/exportInvoices'
import { captureError, captureErrorAndNotify } from '../../utilities/errorHandlers'
import useMutation from '../../hooks/useMutation'
import useQuery, { generateQueryKey } from '../../hooks/useQuery'
import invoicesAmountSummary from '../../graphql/queries/invoicesAmountSummary'
import sendCustomerInvoiceMutation from '../../graphql/mutations/sendCustomerInvoice'
import updateInvoiceMutation from '../../graphql/mutations/updateInvoice'
import notify from '../../utilities/notify'
import Modal from '../../components/modal/Modal'
import SendInvoiceForm from '../../components/send-invoice-form'
import dayjs from 'dayjs'
import { UPDATE_INVOICE_DATA } from './reducer/action'
import { useQueryClient } from 'react-query'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { RECEIVE_ALL_CLIENTS_SUCCESS } from '../hauler/clients/action/clientAction'
import clientsAll from '../../graphql/queries/clientsAll'
import InvoiceSummary from '../../components/invoice/summary'

dayjs.extend(utc)
dayjs.extend(timezone)

const pageSize = 25

export default function InvoicesPage () {
  const [fakeCount, setFakeCount] = useState(null)
  const queryClient = useQueryClient()
  const [invoicesExporting, setInvoicesExporting] = useState(false)
  const { hauler, isWastepayApproved, isWastepayEnabled, invoiceReducerData, user, allClients, dataLoaders } = useSelector(({ user, invoice, allClients, dataLoaders }) => ({
    hauler: user.hauler,
    isWastepayApproved: user.hauler.isWastepayApproved,
    isWastepayEnabled: user.hauler.isWastepayEnabled,
    invoiceReducerData: invoice,
    user,
    dataLoaders,
    allClients
  }))
  const history = useHistory()
  const dispatch = useDispatch()
  const [queryParams, setQueryParams] = useQueryParams({
    sort: ObjectParam,
    status: StringParam,
    pager: ObjectParam,
    invoiceNumber: StringParam,
    accountName: StringParam,
    startDate: StringParam,
    endDate: StringParam
  })
  // Curried function to make sure we are always using 'replace' update.
  const setQueryParamsReplace = useCallback((params) => {
    setQueryParams(params, 'replace')
  }, [setQueryParams])

  const { mutate: exportInvoicesMutation } = useMutation(exportInvoices, {
    onSuccess (data) {
      const email = data?.userEmail
      window.alert(`Export Complete!  The export will be emailed to ${email} with a link to download.`)
    },
    onError () {
      notify('error', 'Error exporting invoices')
    },
    onSettled () {
      setInvoicesExporting(false)
    }
  })

  const closeSendInvoiceModal = useCallback(() => {
    dispatch({ type: UPDATE_INVOICE_DATA, payload: { isModalOpen: false, data: null } })
  }, [dispatch])

  useEffect(() => {
    closeSendInvoiceModal()
  }, [closeSendInvoiceModal])

  function handleInvoiceExport () {
    const {
      sort,
      invoiceNumber,
      status,
      startDate,
      endDate,
      accountName,
      lastSent
    } = queryParams

    if (invoicesExporting) {
      return
    }

    setInvoicesExporting(true)
    return exportInvoicesMutation({
      haulerId: hauler.id,
      sort,
      invoiceNumber,
      status,
      startDate,
      endDate,
      accountName,
      lastSent
    })
  }

  const { data: invoiceSettingsData, isFetching: isFetchingInvoiceSettings } = useQuery([
    QUERY_KEYS.invoiceSettings,
    hauler.id,
    true
  ],
  invoiceSettingsQuery,
  {
    onError: (error) => captureErrorAndNotify(error, 'Error Fetching Invoice Settings')
  })

  const { data: invoices, isFetching: isFetchingInvoices, refetch } = useQuery([
    QUERY_KEYS.invoices,
    hauler.id,
    queryParams.sort,
    queryParams.invoiceNumber,
    queryParams.accountName,
    queryParams.status,
    queryParams.startDate,
    queryParams.endDate,
    queryParams.pager,
    null
  ],
  invoicesQuery,
  {
    enabled: Boolean(queryParams.sort),
    initialData: { invoices: {} },
    // take the result of the invoicesQuery and identify and change the status to 'OVERDUE' before render
    select: ({ invoices }) => (
      identifyOverdueInvoices(invoices)
    )
  })

  const { data: dashboardData, isFetching: isFetchingDashboard, refetch: refetchSummary } = useQuery([
    QUERY_KEYS.invoicesAmountSummary,
    hauler.id,
    queryParams.invoiceNumber,
    queryParams.accountName,
    queryParams.status,
    queryParams.startDate,
    queryParams.endDate,
    null
  ],
  invoicesAmountSummary,
  {
    enabled: Boolean(queryParams.sort),
    initialData: { invoicesAmountSummary: {} },
    onError: (error) => {
      captureErrorAndNotify(error, 'Failed to fetch invoice dashboard data')
    }
  })

  function identifyOverdueInvoices (invoicesList) {
    const currentDate = new Date()

    // Check if invoices.nodes is an array before iterating through it
    if (invoicesList && invoicesList.nodes && Array.isArray(invoicesList.nodes)) {
      const invoicesNodes = invoicesList.nodes.map((invoice) => {
        const dueDate = new Date(invoice.dueDate)
        const isOpen = invoice.status === 'OPEN'

        if (invoiceSettingsData?.invoiceSettings?.sendInvoicesFrom === 'DISPATCHER' && dueDate < currentDate && isOpen) {
          invoice.status = 'OVERDUE'
        }
        return invoice
      })
      invoicesList.nodes = invoicesNodes
    }

    return invoicesList
  }

  function clearForm ({ invoiceNumber, accountName, status, startDate, endDate }) {
    setFakeCount(null)
    setQueryParamsReplace({ invoiceNumber, accountName, status, startDate, endDate })
  }

  function handleSearch ({ invoiceNumber, accountName, status, startDate, endDate }) {
    setFakeCount(null)
    setQueryParamsReplace({ invoiceNumber, accountName, status, startDate, endDate })
  }

  function handleSummaryTabSelect ({ status }) {
    setFakeCount(null)
    setQueryParamsReplace({ ...queryParams, status })
  }

  useEffect(function toggleFakePagination () {
    if (isFetchingInvoices || fakeCount !== null || !invoices.totalCount) return
    setFakeCount({ start: 1, end: Math.min(pageSize, invoices.totalCount) })
  }, [invoices, isFetchingInvoices, fakeCount, setFakeCount])

  function onPageRequest (direction, cursor) {
    setQueryParamsReplace({ ...queryParams, pager: { cursor, direction, pageSize } })

    if (!fakeCount) return
    if (direction === 'before') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start - pageSize, end: prevFakeCount.end - pageSize }))
    }
    if (direction === 'after') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  function onSortChange (newSortColumn) {
    let newSortDirection = 'desc'
    if (newSortColumn === queryParams.sort.column) {
      newSortDirection = queryParams.sort.direction === 'asc' ? 'desc' : 'asc'
    }

    setFakeCount(null)
    setQueryParamsReplace({
      ...{
        invoiceNumber: queryParams.invoiceNumber,
        accountName: queryParams.accountName,
        status: queryParams.status,
        startDate: queryParams.startDate,
        endDate: queryParams.endDate
      },
      sort: { column: newSortColumn, direction: newSortDirection }
    })
  }

  // Always make sure we have the bare minimum query parameters by setting reasonable defaults if not already set.
  useEffect(function setDefaultQueryParams () {
    const defaultQueryParams = {}
    if (!queryParams.sort) {
      defaultQueryParams.sort = { column: 'createdAt', direction: 'desc' }
    }
    setQueryParamsReplace({ ...queryParams, ...defaultQueryParams })
  }, [queryParams, setQueryParamsReplace])

  useEffect(function handleQuickBooksConnection () {
    if (!hauler.canCreateInvoice) {
      history.push(ROUTES.invoiceSettings)
    }
  }, [history, hauler.canCreateInvoice])

  const emailList = useMemo(() => {
    let emailArray = invoiceReducerData.data?.tickets?.flatMap(ticket => [ticket?.job?.email, ticket?.job?.secondaryEmail])
    if (invoiceReducerData.data?.client?.email) {
      emailArray?.push(invoiceReducerData.data?.client.email)
    }
    emailArray = [...new Set(emailArray)]
    return emailArray.filter(email => email != null && email !== '').join(', ')
  }, [invoiceReducerData.data?.tickets, invoiceReducerData.data?.client.email])

  const sendFromDispatcher = invoiceSettingsData?.invoiceSettings?.sendInvoicesFrom === 'DISPATCHER'

  const { mutateAsync: updateInvoice } = useMutation(updateInvoiceMutation, {
    onSuccess ({ invoice: newInvoiceData }, { action }) {
      const queryKey = generateQueryKey([QUERY_KEYS.invoicePaymentDetails, invoiceReducerData.data?.id], user.id)
      queryClient.setQueryData(queryKey, (oldData) => ({
        ...oldData,
        invoice: {
          ...(oldData?.invoice ?? {}),
          ...newInvoiceData
        }
      }))

      if (action !== 'send') {
        notify('success', 'Invoice saved')
        refetch()
        refetchSummary()
      }
    },
    onError (error) {
      captureErrorAndNotify(error, 'Error saving invoice')
    }
  })

  const fetchAllClients = useCallback(() => {
    clientsAll({
      dataLoaders,
      queryKey: [
        QUERY_KEYS.clientsAll,
        hauler.id,
        { column: 'name', direction: 'DESC' },
        null,
        null,
        null,
        null,
        null
      ]
    })
      .then((resp) => {
        dispatch({ type: RECEIVE_ALL_CLIENTS_SUCCESS, payload: { clients: resp.clientsAll } })
      })
      .catch((error) => {
        captureErrorAndNotify(error, 'Error fetching Accounts')
      })
  }, [dataLoaders, dispatch, hauler.id])

  useEffect(() => {
    fetchAllClients()
  }, [fetchAllClients])

  const { mutateAsync: sendCustomerInvoice } = useMutation(sendCustomerInvoiceMutation, {
    onSuccess () {
      refetch()
      refetchSummary()
      notify('success', 'Invoice saved and sent')
    },
    onError (error) {
      captureErrorAndNotify(error, 'Error sending invoice')
    }
  })

  async function handleSendInvoice (data) {
    if (data.action === 'save') {
      closeSendInvoiceModal()
      return updateInvoice({
        id: invoiceReducerData.data?.id,
        ...data
      })
    }

    if (data.action === 'send') {
      closeSendInvoiceModal()
      await updateInvoice({
        id: invoiceReducerData.data?.id,
        ...data
      })
      return sendCustomerInvoice({ invoiceId: invoiceReducerData.data?.id, sendTo: data.billTo })
    }

    captureError(new Error(`Unhandled save invoice action: ${data.action}`))
  }

  return (
    <NormalLayoutContainer>
      <div className={styles.header}>
        <PageTitle>Invoices</PageTitle>
        {(hauler.canCreateInvoice) && <Link to={ROUTES.invoiceNewClient} className='dis-btn dis-btn-lg dis-btn-primary'>
          <span className={styles.newBtnText}>New Invoice</span>
          <i className={cn(styles.newBtnIcon, 'material-icons dis-btn-icon')}>add</i>
        </Link>}
      </div>
      {
        invoiceSettingsData?.invoiceSettings?.sendInvoicesFrom?.toUpperCase() === 'DISPATCHER' &&
          <Modal isOpen={sendFromDispatcher && invoiceReducerData?.isModalOpen} className={styles.modal}>
            <div className={styles.sendInvoiceModal}>
              <section>
                <span>
                  Send Your Invoice
                  {
                    invoiceReducerData?.data?.lastSent !== null &&
                      <p>Last Sent: { dayjs(invoiceReducerData?.data?.lastSent).format('YYYY-MM-DD HH:mm:ss z') }</p>
                  }
                </span>
                <button onClick={closeSendInvoiceModal}>
                  <i className='material-icons'>close</i>
                </button>
              </section>
              <SendInvoiceForm
                onSubmit={handleSendInvoice}
                paymentTerms={invoiceSettingsData?.paymentTerms ?? []}
                isWastepayConnected={hauler.isWastepayConnected}
                invoice={invoiceReducerData?.data}
                initialValues={{
                  date: invoiceReducerData.data?.date ?? dayjs().format('YYYY-MM-DD'),
                  dueDate: invoiceReducerData.data?.dueDate,
                  billTo: invoiceReducerData.data?.billTo ?? emailList,
                  message: invoiceReducerData.data?.message,
                  paymentTerms: invoiceReducerData.data?.paymentTerms ?? invoiceSettingsData?.invoiceSettings.defaultPaymentTerms,
                  includeTerms: invoiceReducerData.data?.includeTerms ?? true,
                  terms: invoiceReducerData.data?.terms ?? invoiceSettingsData?.invoiceSettings?.defaultTerms,
                  includePayNow: invoiceReducerData.data?.includePayNowLink ?? invoiceSettingsData?.invoiceSettings?.includePayNowLink
                }}
                className={styles.sendInvoice}
              />
            </div>
          </Modal>
      }
      <Spinner isFetching={allClients.isFetching}>
        <InvoicesSearchForm
          onSubmit={handleSearch}
          onClear={clearForm}
          isFetching={isFetchingInvoices}
          isWastepayApproved={isWastepayApproved}
          isWastepayEnabled={isWastepayEnabled}
          clients={allClients.clients}
          initialValues={{
            invoiceNumber: queryParams.invoiceNumber,
            accountName: queryParams.accountName,
            status: queryParams.status,
            startDate: queryParams.startDate,
            endDate: queryParams.endDate
          }}
          handleExport={handleInvoiceExport}
          isExporting={invoicesExporting}
          invoiceProvider={invoiceSettingsData?.invoiceSettings?.sendInvoicesFrom ?? ''}
        />
      </Spinner>
      {
        isFetchingDashboard || !Object.keys(dashboardData.invoicesAmountSummary).length
          ? <Spinner isFetching />
          : (
            <InvoiceSummary
              onSubmit={handleSummaryTabSelect}
              activeStatus={queryParams.status}
              summaryData={dashboardData?.invoicesAmountSummary}
            />
            )
      }
      {
        isFetchingInvoiceSettings || isFetchingInvoices || !Object.keys(invoices).length
          ? <Spinner isFetching />
          : (
            <InvoicesTable
              invoices={invoices}
              onPageRequest={onPageRequest}
              onSortChange={onSortChange}
              fakeCount={fakeCount}
              sort={queryParams.sort}
              hauler={hauler}
              invoiceProvider={invoiceSettingsData?.invoiceSettings?.sendInvoicesFrom ?? ''}
            />
            )
      }
    </NormalLayoutContainer>
  )
}

InvoicesPage.propTypes = {
}
